/**
 * Workspace dashboard — fully AI-composable.
 *
 * The dashboard renders entirely through PanelRenderer, with two paths:
 *
 *   1. AI panel composed at workspaces.ai_panels.dashboard.default →
 *      that PanelSpec wins. Full takeover. The AI controls every
 *      visible section, in any order, with any new content interleaved.
 *
 *   2. No AI panel composed → DEFAULT_DASHBOARD_SPEC renders. It's the
 *      same vocabulary the AI uses (a column of widget kinds), so the
 *      AI can read it, mutate it, and ship a tweak via compose_panel
 *      without re-deriving the full layout from scratch.
 *
 * Each built-in section is now a widget_id under "dashboard.*" — the
 * narration, workspace summary, attention items, tracked bills, top
 * relationships, AI feed, activity stream, and the legacy widget grid.
 * The AI references these by widget_id inside its PanelSpec.
 *
 * The only fixed chrome is SinceLastSeen (auto-shown banner that
 * self-hides) and the coalition-workspace redirect.
 */
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Tv, Kanban, ScrollText, Users, Wallet, ShieldAlert } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { useWorkspace } from "../hooks/useWorkspace";
import { usePageContext } from "../hooks/usePageContext";
import { useAIPanel } from "../hooks/useAIPanel";
import { ButtonLink, PageHeader, SkeletonCards } from "../components/ui";
import { PanelRenderer, SinceLastSeen, type PanelSpec } from "../../framework/react/index.js";
import { appRouterPath, tvModePath, withWorkspace } from "../../lib/page-links";
import { WIDGET_REGISTRY } from "../widgets/registry";
import { DashboardStats } from "../widgets/dashboard/dashboard-widgets";

interface KickoffPayload {
  user: { id: string; email: string | null; name: string | null; tier: string | null };
  workspaces: Array<{
    id: string;
    name: string;
    type: string;
    current_focus: string | null;
    tracked_bills: number;
    pending_homework: number;
    pending_homework_assigned_to_you: number;
    recent_actions_7d: number;
  }>;
  top_attention: Array<{
    id: string;
    workspace_id: string;
    workspace_name: string | null;
    summary: string | null;
    why_user: string | null;
    is_assigned_to_you: boolean;
  }>;
  starter_suggestions: string[];
  narration: string;
}

const resolveWorkspaceWidget = (widget_id: string) =>
  WIDGET_REGISTRY[widget_id] ?? null;

/**
 * Canonical default dashboard layout. Encoded as a PanelSpec so the
 * default and AI-composed paths share the same renderer. The AI can
 * `list_panels` to read this (when no panel is composed, list_panels
 * returns nothing — the AI can ask for the default spec or copy this
 * structure from the master prompt).
 *
 * Hierarchy: what needs you (attention queue + briefing) → recent
 * activity (AI feed, hidden when empty, then the workspace event
 * stream) → the workspace's working set (bills, relationships, policy
 * intelligence, scorecards) → the legacy widget grid (hidden when empty). The workspace stats row sits above
 * this spec in DashboardPage and is not part of it.
 */
export const DEFAULT_DASHBOARD_SPEC: PanelSpec = {
  kind: "column",
  gap: 5,
  children: [
    // Section headers wrap each group so the page emits headings the
    // render-snapshot DOM extractor + visible_summary text fallback key
    // off — without them the AI's "what's on the screen" signal
    // degraded to just the visible-object keys (FB filed during testing).
    // Widgets carry their own card titles, so headers name sections only.
    { kind: "header", level: 2, text: "Needs attention" },
    {
      kind: "row",
      gap: 4,
      align: "stretch",
      children: [
        { kind: "widget", widget_id: "dashboard.top_attention" },
        { kind: "widget", widget_id: "dashboard.narration" },
      ],
    },
    { kind: "header", level: 2, text: "Recent activity" },
    { kind: "widget", widget_id: "dashboard.feed" },
    { kind: "widget", widget_id: "dashboard.activity_stream" },
    { kind: "header", level: 2, text: "Your work" },
    {
      kind: "row",
      gap: 4,
      align: "stretch",
      children: [
        { kind: "widget", widget_id: "dashboard.tracked_bills" },
        { kind: "widget", widget_id: "dashboard.top_relationships" },
      ],
    },
    { kind: "widget", widget_id: "dashboard.policy_intelligence" },
    { kind: "widget", widget_id: "dashboard.scorecards" },
    { kind: "widget", widget_id: "dashboard.widget_grid", params: { hide_when_empty: true } },
  ],
};

export function DashboardPage() {
  const { current } = useWorkspace();

  // CRITICAL: keep all hooks above any early returns. React enforces
  // a stable hook call order across renders — a `return <Navigate/>`
  // between hooks would crash with "Rendered fewer hooks than
  // expected" the moment the redirect condition flipped to true.

  // Detect coalition workspaces so we can redirect to /coalition-feed.
  const { data: wsInfo } = useQuery({
    queryKey: ["workspace-info", current?.id],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${current!.id}/info`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!current,
  });

  const { data: kickoff } = useQuery({
    queryKey: ["session-kickoff"],
    queryFn: async () => {
      const res = await fetch("/api/v1/session-kickoff", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch session kickoff");
      return (await res.json()) as KickoffPayload;
    },
  });

  // Pull the AI-composed panel spec, if any. When set, it wins over
  // the default — full dashboard takeover.
  const { data: aiPanel, isLoading: panelLoading } = useAIPanel(current?.id, "dashboard", "default");
  const composedSpec = aiPanel?.spec as PanelSpec | undefined;

  // Snapshot the dashboard for the floating chat AI: where the user is
  // (current workspace), what's in their attention queue, what tracked
  // bills they have, what was suggested. Lets "what should I work on
  // first" / "summarize my pending work" answer from on-screen state.
  const wsRow = current ? kickoff?.workspaces.find((w) => w.id === current.id) ?? null : null;
  usePageContext(
    {
      kind: "dashboard",
      supports: ["navigate"],
      // Hint to the AI that this page is the destination for
      // render_dashboard_panel — when the user asks "show me X" while
      // here, render INTO the dashboard feed, not a chat-panel card.
      panel_feed: true,
      // Tell the AI whether the dashboard is currently AI-composed or
      // running the default. compose_panel takes over; clear_panel
      // restores the default.
      dashboard_layout_source: composedSpec ? "ai_composed" : "default",
      visible: () => ({
        current_workspace: wsRow ? {
          id: wsRow.id,
          name: wsRow.name,
          type: wsRow.type,
          current_focus: wsRow.current_focus,
          tracked_bills: wsRow.tracked_bills,
          pending_homework: wsRow.pending_homework,
          pending_homework_assigned_to_you: wsRow.pending_homework_assigned_to_you,
          recent_actions_7d: wsRow.recent_actions_7d,
        } : null,
        narration: kickoff?.narration ?? null,
        starter_suggestions: kickoff?.starter_suggestions?.slice(0, 4) ?? [],
        top_attention: kickoff?.top_attention?.slice(0, 8).map((a) => ({
          id: a.id,
          workspace_name: a.workspace_name,
          summary: a.summary,
          why_user: a.why_user,
          is_assigned_to_you: a.is_assigned_to_you,
        })) ?? [],
      }),
    },
    [current?.id, !!kickoff, !!composedSpec]
  );

  // Coalition workspaces have a feed-first dashboard at /coalition-feed.
  if (wsInfo?.workspace?.workspace_type === "coalition" && !panelLoading && !composedSpec) {
    return <Navigate to="/coalition-feed" replace />;
  }

  // The spec to render: AI's composition wins; otherwise the default.
  const spec: PanelSpec = composedSpec ?? DEFAULT_DASHBOARD_SPEC;

  return (
    <div className="cmcp-page space-y-6">
      <PageHeader
        breadcrumb={[]}
        title="Dashboard"
        subtitle={current
          ? `What needs you, what changed and what's moving in ${current.name}.`
          : "Your workspace at a glance: what needs you, what changed and what's moving."}
        actions={<>
          {current && <ButtonLink variant="ghost" icon={Tv} to={appRouterPath(withWorkspace(tvModePath("dashboard", "default"), current.id))}>TV mode</ButtonLink>}
          <ButtonLink icon={Sparkles} to={`/reports?workspace_id=${current?.id ?? ""}`}>Customize with AI</ButtonLink>
        </>}
      />
      {/* Workspace stats row — fixed chrome above the default layout. An
          AI composition is a full takeover, so it's hidden then. */}
      {current && !panelLoading && !composedSpec && <DashboardStats workspaceId={current.id} />}
      {current && !panelLoading && !composedSpec && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 text-xs">
          <Link to="/whip-boards" className="cmcp-card p-3 hover:border-ink transition-colors flex items-center gap-2.5">
            <Kanban size={15} className="text-action shrink-0" aria-hidden />
            <div>
              <div className="font-semibold text-ink">50-State &amp; Federal Whips</div>
              <div className="text-[11px] text-stone-500">Live chamber boards &amp; tallies</div>
            </div>
          </Link>
          <Link to="/discover" className="cmcp-card p-3 hover:border-ink transition-colors flex items-center gap-2.5">
            <ScrollText size={15} className="text-action shrink-0" aria-hidden />
            <div>
              <div className="font-semibold text-ink">Redlines &amp; Passage Sim</div>
              <div className="text-[11px] text-stone-500">Statute diffs, p_law &amp; impact</div>
            </div>
          </Link>
          <Link to="/members" className="cmcp-card p-3 hover:border-ink transition-colors flex items-center gap-2.5">
            <ShieldAlert size={15} className="text-action shrink-0" aria-hidden />
            <div>
              <div className="font-semibold text-ink">Oppo, Stocks &amp; Sleepers</div>
              <div className="text-[11px] text-stone-500">Contradictions, PTRs &amp; cue-givers</div>
            </div>
          </Link>
          <Link to="/companies" className="cmcp-card p-3 hover:border-ink transition-colors flex items-center gap-2.5">
            <Users size={15} className="text-action shrink-0" aria-hidden />
            <div>
              <div className="font-semibold text-ink">Tickers, Companies &amp; LDA</div>
              <div className="text-[11px] text-stone-500">Ticker exposure &amp; filings</div>
            </div>
          </Link>
          <Link to="/treasury" className="cmcp-card p-3 hover:border-ink transition-colors flex items-center gap-2.5">
            <Wallet size={15} className="text-action shrink-0" aria-hidden />
            <div>
              <div className="font-semibold text-ink">Treasury &amp; Pipelines</div>
              <div className="text-[11px] text-stone-500">501(c)/PAC ledgers &amp; deals</div>
            </div>
          </Link>
        </div>
      )}
      {/* "Since you were here" — auto-shown only when there's actual
          news. Stays outside the AI takeover surface; it's a session
          banner, not a layout choice. */}
      <SinceLastSeen workspaceId={current?.id ?? null} />

      {panelLoading && <SkeletonCards count={3} label="Loading dashboard…" />}
      {current && !panelLoading && (
        <PanelRenderer
          spec={spec}
          workspaceId={current.id}
          resolveWidget={resolveWorkspaceWidget}
        />
      )}
    </div>
  );
}
